/**
 * useClickOutside Hook
 * Detects clicks outside a specified element
 * Following LLM-First Architecture principles
 */

import { useEffect, RefObject } from 'react'

export interface UseClickOutsideOptions {
  /** Whether the hook is active */
  enabled?: boolean
  /** Additional callback for when click is detected outside */
  onClickOutside?: (event: MouseEvent | TouchEvent) => void
}

/**
 * Hook to detect clicks outside a referenced element
 *
 * @example
 * ```tsx
 * const ref = useRef<HTMLDivElement>(null);
 * useClickOutside(ref, () => setIsOpen(false));
 * ```
 */
export function useClickOutside<T extends HTMLElement>(
  ref: RefObject<T>,
  callback: (event: MouseEvent | TouchEvent) => void,
  options: UseClickOutsideOptions = {},
): void {
  const { enabled = true } = options

  useEffect(() => {
    if (!enabled) return

    const handleClick = (event: MouseEvent | TouchEvent) => {
      const target = event.target as Node

      // Check if click is outside the referenced element
      if (ref.current && !ref.current.contains(target)) {
        callback(event)
        options.onClickOutside?.(event)
      }
    }

    // Handle both mouse and touch events
    document.addEventListener('mousedown', handleClick)
    document.addEventListener('touchstart', handleClick)

    return () => {
      document.removeEventListener('mousedown', handleClick)
      document.removeEventListener('touchstart', handleClick)
    }
  }, [ref, callback, enabled, options])
}
